import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrips } from "@/hooks/useTrips";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Route, MapPin, Wallet, MessageSquare,
  ChevronRight, ChevronLeft, Sparkles, X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/lib/i18n";

const ONBOARDING_KEY = "vantastika_onboarding_done";

interface Step {
  icon: typeof Route;
  emoji: string;
  titleKey: string;
  descKey: string;
  color: string;
  actionLabelKey: string;
  actionPath: string;
}

const STEPS: Step[] = [
  {
    icon: Route, emoji: "🗺️", titleKey: "onboarding.planTrip", descKey: "onboarding.planTripDesc",
    color: "bg-primary/10 text-primary", actionLabelKey: "onboarding.createFirstTrip", actionPath: "/trips",
  },
  {
    icon: MapPin, emoji: "🏕️", titleKey: "onboarding.findPlaces", descKey: "onboarding.findPlacesDesc",
    color: "bg-meadow/10 text-meadow", actionLabelKey: "onboarding.searchCampings", actionPath: "/search",
  },
  {
    icon: Wallet, emoji: "💰", titleKey: "onboarding.budgetChecklist", descKey: "onboarding.budgetChecklistDesc",
    color: "bg-accent/15 text-accent-foreground", actionLabelKey: "onboarding.createTrip", actionPath: "/trips",
  },
  {
    icon: MessageSquare, emoji: "🤖", titleKey: "onboarding.askVannie", descKey: "onboarding.askVannieDesc",
    color: "bg-sunrise/10 text-sunrise", actionLabelKey: "onboarding.doneStart", actionPath: "",
  },
];

const OnboardingGuide = React.forwardRef<HTMLDivElement>(function OnboardingGuide(_, _ref) {
  const { user } = useAuth();
  const { trips } = useTrips();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (done) return;

    // Never show on trip-related pages if user already has trips
    const tripPages = ["/my-trips", "/trips"];
    if (tripPages.includes(location.pathname) && trips.length > 0) return;

    // Never show on active trip or detail pages
    if (location.pathname.startsWith("/trip/") || location.pathname === "/search") return;

    // Only show on home or trips pages for genuinely new users
    const allowedPages = ["/", "/my-trips", "/trips"];
    if (!allowedPages.includes(location.pathname)) return;

    // Wait for GDPR consent first
    const check = () => {
      const consent = localStorage.getItem("gdpr-consent");
      if (consent) {
        const timer = setTimeout(() => setOpen(true), 1200);
        return timer;
      }
      return null;
    };
    let timer = check();
    if (!timer) {
      const interval = setInterval(() => {
        timer = check();
        if (timer) clearInterval(interval);
      }, 1000);
      return () => { clearInterval(interval); if (timer) clearTimeout(timer); };
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [user, location.pathname, trips.length]);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  const handleAction = () => {
    const current = STEPS[step];
    handleClose();
    if (current.actionPath) navigate(current.actionPath);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else handleClose();
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!user) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-border/50 gap-0">
        <button onClick={handleClose} className="absolute right-3 top-3 z-10 p-1.5 rounded-full hover:bg-muted/50 transition-colors text-muted-foreground" aria-label={t("aria.close")}>
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 px-6 pt-8 pb-6 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }} className="inline-flex items-center gap-2 text-sm font-bold text-primary bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4" />
            {t("onboarding.step").replace("{current}", String(step + 1)).replace("{total}", String(STEPS.length))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="flex flex-col items-center">
              <div className={`h-16 w-16 rounded-2xl ${current.color} flex items-center justify-center mb-4`}>
                <span className="text-3xl">{current.emoji}</span>
              </div>
              <h3 className="text-xl font-bold text-foreground">{t(current.titleKey)}</h3>
              <p className="text-muted-foreground mt-3 max-w-xs leading-relaxed">{t(current.descKey)}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="px-6 pb-6 pt-4 space-y-4">
          <div className="flex items-center justify-center gap-2">
            {STEPS.map((_, i) => (
              <button key={i} onClick={() => setStep(i)} className={`h-2.5 rounded-full transition-all duration-300 ${i === step ? "w-7 bg-primary" : i < step ? "w-2.5 bg-primary/40" : "w-2.5 bg-muted-foreground/20 hover:bg-muted-foreground/40"}`} aria-label={`${t("aria.step")} ${i + 1}`} />
            ))}
          </div>

          <Button onClick={handleAction} className="w-full font-bold text-base h-12" size="lg">
            {t(current.actionLabelKey)}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>

          <div className="flex items-center justify-between">
            {step > 0 ? (
              <Button variant="ghost" size="sm" onClick={handlePrev} className="font-semibold">
                <ChevronLeft className="h-4 w-4 mr-1" />{t("onboarding.back")}
              </Button>
            ) : <div />}
            {!isLast ? (
              <Button variant="ghost" size="sm" onClick={handleNext} className="font-semibold">
                {t("onboarding.skip")}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default OnboardingGuide;
